import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  addedEntries,
  entriesToText,
  isRemovalOnly,
  matchesStored,
  MAX_BLOCKED_DOMAIN_ENTRIES,
  parseBlockedDomainEntries,
} from './blockedDomainsUtils'

import styles from './NotetakerBlockedDomainsModal.module.scss'

import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import Dialog from '@/components/Dialog'
import TextArea from '@/components/TextArea'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { AppDispatch } from '@/store'
import { updateNotetakerBlockedDomainsThunk } from '@/store/features/user/userSlice'
import { NotetakerBlockedDomain, SettingType } from '@/types'

interface NotetakerBlockedDomainsModalProps {
  isOpen: boolean
  close: () => void
  currentRules: NotetakerBlockedDomain[]
  isAdmin: boolean
  authoringEnabled?: boolean
}

type Phase = 'editing' | 'saving'

const SELF_BLOCK_CODE = 'notetaker_blocked_domains_self_block'

const NotetakerBlockedDomainsModal = ({
  isOpen,
  close,
  currentRules,
  isAdmin,
  authoringEnabled = true,
}: NotetakerBlockedDomainsModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { updateToast } = useToast()

  const [text, setText] = useState(() => entriesToText(currentRules))
  const [phase, setPhase] = useState<Phase>('editing')
  const [apiError, setApiError] = useState<string | null>(null)
  const [selfBlockConflicts, setSelfBlockConflicts] = useState<string[] | null>(null)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return

    setText(entriesToText(currentRules))
    setPhase('editing')
    setApiError(null)
    setSelfBlockConflicts(null)
    setShowDiscardConfirm(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const entries = useMemo(() => parseBlockedDomainEntries(text), [text])
  const isDirty = !matchesStored(entries, currentRules)
  const additions = useMemo(() => addedEntries(entries, currentRules), [entries, currentRules])

  // Only what the server can't already tell us: which entries are valid, and how each
  // one classifies, is the server's call by design — a local guess could disagree with
  // what actually gets stored.
  const validationError =
    entries.length > MAX_BLOCKED_DOMAIN_ENTRIES
      ? `Enter at most ${MAX_BLOCKED_DOMAIN_ENTRIES} entries (you have ${entries.length}).`
      : null

  const saveBlockedReason = (() => {
    if (!isAdmin) return 'Only admins can edit blocked domains.'
    if (validationError) return validationError
    if (!isDirty) return 'No changes to save.'
    if (!authoringEnabled && !isRemovalOnly(entries, currentRules)) {
      return 'Adding new domains is not enabled for this organization. You can still remove existing ones.'
    }
    return null
  })()

  const statusMessage = (() => {
    if (apiError) return null
    if (saveBlockedReason && saveBlockedReason !== 'No changes to save.') return saveBlockedReason
    return null
  })()

  const doSave = async (acknowledgeSelfBlock = false) => {
    setPhase('saving')
    setApiError(null)
    try {
      await dispatch(
        updateNotetakerBlockedDomainsThunk({
          settings: { [SettingType.notetakerBlockedDomains]: entries },
          ...(acknowledgeSelfBlock ? { acknowledgeSelfBlock: true } : {}),
        })
      ).unwrap()
      updateToast('Blocked domains updated', 'success')
      setSelfBlockConflicts(null)
      close()
    } catch (error) {
      const rejection = error as { message?: string; code?: string; conflicts?: string[] } | undefined
      // Recoverable: blocking a subsidiary's domain is legitimate, so this confirms
      // rather than refuses.
      if (rejection?.code === SELF_BLOCK_CODE) {
        setSelfBlockConflicts(rejection.conflicts?.length ? rejection.conflicts : additions)
        setPhase('editing')
        return
      }
      setApiError(rejection?.message ?? 'Failed to save. Please try again.')
      setPhase('editing')
    }
  }

  // The inline error names a specific bad cell, so it stops being true the moment the
  // admin edits the field — and it also suppresses `statusMessage` while it stands.
  const handleInput = (value: string) => {
    if (apiError) setApiError(null)
    setText(value)
  }

  const handleClose = () => {
    if (phase === 'saving') return
    if (isDirty) {
      setShowDiscardConfirm(true)
      return
    }
    close()
  }

  const handleDiscard = () => {
    setShowDiscardConfirm(false)
    close()
  }

  const saveButton = (
    <Button
      title="Save"
      onClick={() => void doSave()}
      type="dark"
      isDisabled={saveBlockedReason !== null || phase === 'saving'}
      isLoading={phase === 'saving'}
    />
  )

  const dialogHidden = showDiscardConfirm || selfBlockConflicts !== null

  return (
    <>
      <Dialog
        isOpen={isOpen && !dialogHidden}
        title="Edit blocked domains"
        close={handleClose}
        minWidth="min(560px, calc(100vw - 64px))"
      >
        <div className={styles.modalBody}>
          <p className={styles.helpText}>
            Configure email domains, which, when present on a linked calendar invite, block members from using Notetaker
            for that meeting.
          </p>
          <p className={styles.helpText}>
            Enter emails separated by a comma. Use *@domain.com to block everyone with that domain.
          </p>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="notetaker-blocked-domains-input">
              Blocked domains
            </label>
            <TextArea
              id="notetaker-blocked-domains-input"
              className={styles.textArea}
              value={text}
              onInput={handleInput}
              rows={8}
              placeholder="*@lawfirm.com, markus@wispr.ai"
              isDisabled={phase === 'saving' || !isAdmin}
            />
          </div>

          <div className={styles.errorSlot}>
            {apiError && (
              <p className={styles.errorText} role="alert">
                {apiError}
              </p>
            )}
            {statusMessage && (
              <p className={styles.statusText} role="status">
                {statusMessage}
              </p>
            )}
          </div>

          <div className={styles.modalActions}>
            <Button title="Cancel" onClick={handleClose} type="light" isDisabled={phase === 'saving'} />
            {saveBlockedReason !== null ? (
              <Tooltip text={saveBlockedReason} placement="top" hideArrow>
                {saveButton}
              </Tooltip>
            ) : (
              saveButton
            )}
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={selfBlockConflicts !== null}
        title="These rules also block your own members"
        message={`${(selfBlockConflicts ?? []).join(', ')} — ${
          (selfBlockConflicts ?? []).length === 1 ? 'this matches' : 'these match'
        } your organization's own domains or a consumer mail provider, so meetings with your own people would stop recording. Save anyway?`}
        confirmLabel="Save anyway"
        cancelLabel="Go back"
        onConfirm={() => {
          setSelfBlockConflicts(null)
          void doSave(true)
        }}
        onCancel={() => setSelfBlockConflicts(null)}
      />

      <ConfirmDialog
        isOpen={showDiscardConfirm}
        title="Discard unsaved changes?"
        message="Your changes to blocked domains haven't been saved. If you discard now, you'll lose them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        confirmType="destructive"
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  )
}

export default NotetakerBlockedDomainsModal
